// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import {
  getFavoritosUsuario,
  eliminarFavorito,
} from "@/features/eventosFavoritos/eventosFavoritosSlice";

// React Utils
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

// Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Utils
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { scrollToTop } from "@/lib/utilsUI";

export default function Favoritos() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { favoritos, isLoading } = useSelector((state) => state.eventosFavoritos);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Load favorites on mount
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getFavoritosUsuario());
    }
  }, [dispatch, isAuthenticated]);

  const handleEliminarFavorito = async (eventoUid) => {
    try {
      await dispatch(eliminarFavorito(eventoUid)).unwrap();
      // Refresh the list
      dispatch(getFavoritosUsuario());
    } catch (error) {
      console.error("Error al eliminar favorito:", error);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1">Mis Favoritos</h1>
        <p className="text-gray-400 text-sm">
          Eventos que has marcado como favoritos
        </p>
      </div>

      {isLoading ? (
        <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
              <div key={index}>
                <Skeleton height={200} borderRadius={8} className="mb-2" />
                <Skeleton height={16} className="mb-1" />
                <Skeleton height={14} width="60%" />
              </div>
            ))}
          </div>
        </SkeletonTheme>
      ) : favoritos.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {favoritos.map((evento, index) => (
            <div
              key={evento.uid || index}
              className="group flex flex-col rounded-xl overflow-hidden bg-aw-bluedark border border-gray-700 hover:border-aw-green transition-colors relative"
            >
              {/* Remove from favorites button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleEliminarFavorito(evento.uid);
                }}
                className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center shadow transition-colors"
                title="Quitar de favoritos"
              >
                <FontAwesomeIcon icon="fa-solid fa-heart-crack" className="text-xs" />
              </button>

              <Link to={`/evento/${evento.uid}`} onClick={scrollToTop} className="flex flex-col flex-1">
                <div className="w-full aspect-[3/4] overflow-hidden">
                  {evento.img ? (
                    <img
                      src={evento.img}
                      alt={evento.nombre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-aw-blueblack flex items-center justify-center">
                      <FontAwesomeIcon
                        icon="fa-solid fa-calendar-days"
                        className="text-4xl text-gray-600"
                      />
                    </div>
                  )}
                </div>
                <div className="p-2 flex-1">
                  <p className="text-white text-sm font-semibold line-clamp-2 mb-1">
                    {evento.nombre}
                  </p>
                  {evento.empresaNombre && (
                    <p className="text-gray-400 text-xs line-clamp-1">
                      {evento.empresaNombre}
                    </p>
                  )}
                  {evento.fechaHoraInicio && (
                    <p className="text-aw-green text-xs mt-1">
                      {formatFechaHoraEvento(new Date(evento.fechaHoraInicio))}
                    </p>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <FontAwesomeIcon
            icon="fa-regular fa-heart"
            className="text-6xl mb-4 opacity-30"
          />
          <h2 className="text-xl font-semibold mb-2 text-white">
            No tienes eventos favoritos
          </h2>
          <p className="text-center text-sm mb-6 max-w-sm">
            Aun no tienes eventos favoritos. Explora los eventos disponibles y marca tus favoritos para encontrarlos facilmente aqui.
          </p>
          <Link
            to="/"
            className="px-6 py-3 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-105 transition-transform"
            onClick={scrollToTop}
          >
            Explorar Eventos
          </Link>
        </div>
      )}
    </div>
  );
}
